import type { FieldDefinition } from '@/shared/components/query-builder'
import type {
	PricingCampaignModel,
	ProductAssignmentModel,
} from '@/shared/generated'

import { useMemo } from 'react'
import { useIsFetching } from '@tanstack/react-query'
import { isNil, prop } from 'lodash/fp'
import { GripVerticalIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
	createChoices,
	isFilterbaleField,
	isPreproField,
	toAdaptedSchema,
	useColumnsSchemaQuery,
} from '@/entities/column'
import { useBrandsQuery, useCategoriesQuery } from '@/entities/core'
import { useOptimizationGroupsQuery } from '@/entities/optimization-group'
import {
	EngineBadge,
	getPricingCampaignEngine,
	GlobalPcBadge,
	isGlobalPricingCampaign,
} from '@/entities/pricing-campaign'

import {
	getQueryDescription,
	Operators,
	ValueEditorType,
	ValueType,
} from '@/shared/components/query-builder'
import { Card } from '@/shared/components/ui/card'
import { LinkButton } from '@/shared/components/ui/link-button'
import {
	Sortable,
	SortableContent,
	SortableItem,
	SortableItemHandle,
	SortableOverlay,
} from '@/shared/components/ui/sortable'
import { Spinner } from '@/shared/components/ui/spinner'
import { Tag } from '@/shared/components/ui/tag'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { HiddenOnContext } from '@/shared/generated'
import { ampli } from '@/shared/lib/ampli'
import { useModal } from '@/shared/lib/modal-manager'
import { formatNumber } from '@/shared/tools/locale'

export function PricingCampaignsPriority({
	predicts,
	pricingCampaigns,
	isItemDragDisabled,
	onChange,
}: {
	predicts: {
		data?: Record<string, number>
		isLoading: boolean
	}
	pricingCampaigns: PricingCampaignModel[]
	isItemDragDisabled?(item: PricingCampaignModel): boolean
	onChange: (items: PricingCampaignModel[]) => void
}) {
	const { t } = useTranslation()

	return (
		<Sortable
			value={pricingCampaigns}
			getItemValue={(item) => item.id}
			onValueChange={(newOrder) => {
				const initialOrders = pricingCampaigns.map(prop('order'))
				ampli.pcsOrderChnaged()
				onChange(
					newOrder.map((item, i) => ({
						...item,
						order: initialOrders[i]!,
					}))
				)
			}}
		>
			<SortableContent className='flex w-full flex-col gap-2'>
				{pricingCampaigns.map((pricingCampaign) => {
					const isDisabled = isItemDragDisabled?.(pricingCampaign)

					return (
						<SortableItem
							key={pricingCampaign.id}
							value={pricingCampaign.id}
							disabled={isDisabled}
							asChild
						>
							<Card className='flex-row items-stretch gap-2 overflow-hidden rounded-md p-0'>
								{isDisabled ? (
									<Tooltip>
										<TooltipTrigger asChild>
											<div className='flex cursor-not-allowed items-center justify-center bg-muted text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground aria-pressed:cursor-grabbing'>
												<SortableItemHandle>
													<GripVerticalIcon className='size-4' />
												</SortableItemHandle>
											</div>
										</TooltipTrigger>
										<TooltipContent side='left'>
											<p className='w-64'>
												{t('pc.ordering.disabled.hint', {
													defaultValue:
														'This is global pricing campaign. To change global priority, please go to the global pricing camapigns priority settings.',
												})}
											</p>
										</TooltipContent>
									</Tooltip>
								) : (
									<div className='flex cursor-grab items-center justify-center bg-muted text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground aria-pressed:cursor-grabbing'>
										<SortableItemHandle>
											<GripVerticalIcon className='size-4' />
										</SortableItemHandle>
									</div>
								)}
								<PricingCampaignItem
									pricingCampaign={pricingCampaign}
									count={predicts.data?.[pricingCampaign.id]}
								/>
							</Card>
						</SortableItem>
					)
				})}
			</SortableContent>
			<SortableOverlay>
				{({ value }) => {
					const activeItem = pricingCampaigns.find((item) => item.id === value)
					if (!activeItem) {
						return null
					}

					return (
						<Card className='flex-row items-stretch gap-2 overflow-hidden rounded-md p-0'>
							<div className='flex cursor-grab items-center justify-center bg-muted text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground aria-pressed:cursor-grabbing'>
								<GripVerticalIcon className='size-4' />
							</div>
							<PricingCampaignItem pricingCampaign={activeItem} />
						</Card>
					)
				}}
			</SortableOverlay>
		</Sortable>
	)
}
const PricingCampaignItem = ({
	pricingCampaign,
	count,
}: {
	pricingCampaign: PricingCampaignModel
	count?: number
}) => {
	const { t } = useTranslation()

	const isFetching = useIsFetching({
		queryKey: ['matched-products'],
	})
	const modal = useModal('PRICING_CAMPAIGN_MODAL')
	const engine = getPricingCampaignEngine(pricingCampaign)

	return (
		<div className='flex w-full items-center gap-2 overflow-hidden'>
			<Tag>{pricingCampaign.order}</Tag>
			<div className='flex w-full flex-col justify-center gap-1 overflow-hidden py-2 pr-2'>
				<div className='flex w-full items-center gap-2'>
					<LinkButton
						className='text-sm'
						onClick={() =>
							modal.open({
								pricingCampaignId: pricingCampaign.id,
								optimizationGroupId: pricingCampaign.optimization_group_id,
								scenarioId: pricingCampaign.scenario_id,
							})
						}
					>
						{pricingCampaign.name}
					</LinkButton>

					<EngineBadge size='sm' engine={engine}>
						{t(engine, { defaultValue: engine })}
					</EngineBadge>
					{isGlobalPricingCampaign(pricingCampaign) && <GlobalPcBadge />}
					<span className='ml-auto font-mono text-sm font-medium'>
						{isFetching ? (
							<Spinner />
						) : (
							!isNil(count) && (
								<span>{formatNumber(Math.floor(count || 0))}</span>
							)
						)}
					</span>
				</div>
				<div className='flex flex-1 flex-wrap gap-1'>
					{pricingCampaign.settings.product_assignments?.length > 0 ? (
						<span className='truncate text-sm text-muted-foreground'>
							<AssignmentsList
								assignments={pricingCampaign.settings.product_assignments}
							/>
						</span>
					) : (
						<span className='truncate text-sm text-muted-foreground'>-</span>
					)}
				</div>
			</div>
		</div>
	)
}
function AssignmentsList({
	assignments,
}: {
	assignments: ProductAssignmentModel
}) {
	const { t } = useTranslation()

	const { data: brand } = useBrandsQuery({})
	const { data: category } = useCategoriesQuery({})
	const { data: optimization_group } = useOptimizationGroupsQuery()
	const fieldsQuery = useColumnsSchemaQuery(HiddenOnContext.Assortment, {
		select: (data) => data.filter(isPreproField).filter(isFilterbaleField),
	})
	const entities = useMemo(
		() => ({
			category: category || [],
			brand: brand || [],
			optimization_group: optimization_group || [],
		}),
		[category, brand, optimization_group]
	)
	const dataChoices = useMemo(() => {
		const schema = fieldsQuery.data || []

		return createChoices({
			schema,
			entities,
		})
	}, [entities, fieldsQuery.data])
	const options = useMemo(() => {
		let fields: FieldDefinition[] = []
		if (fieldsQuery.data?.length) {
			fields = [
				{
					enum: [],
					label: t('field_schema_optimization_group', { ns: 'fields' }),
					value: 'optimization_group_id',
					valueEditorType: ValueEditorType.multiselect,
					valueType: ValueType.string,
					operations: [Operators.IN, Operators.NOT_IN],
				},
				...(fieldsQuery.data || []).map((schema) => toAdaptedSchema(schema, t)),
			]
		}

		return {
			fields,
			dataChoices,
		}
	}, [fieldsQuery.data, dataChoices, t])

	return useMemo(
		() =>
			assignments
				.reduce((acc, assignment) => {
					acc.push(
						getQueryDescription(assignment?.filters || [], options)
							.map(
								(item) =>
									`${item?.label} ${item?.operation ? item?.operation : ''} ${item?.value ? item?.value : ''}`
							)
							.join(' AND ')
					)

					return acc
				}, [] as string[])
				.join(' OR '),
		[assignments, options]
	)
}
